import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CardContent,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message } from "antd";
import { useTranslation } from "react-i18next";

const AddUser = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = React.useState("");
  const [role, setRole] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [branch_id, setBranch_id] = React.useState("");
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";
  const token = localStorage.getItem("token");
  const [userRole, setUserRole] = React.useState("");
  // eslint-disable-next-line no-unused-vars
  const [userBranch, setUserBranch] = React.useState("");
  React.useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        setUserBranch(user.branch_id);

        // Set branch_id automatically for non-superadmins
        if (user.role !== "superadmin") {
          setBranch_id(user.branch_id);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const requestBody = {
        username,
        password,
        role,
        branch_id,
      };

      await axios.post("https://api.clever-school.com/api/users", requestBody, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      message.success(t("userCreatedSuccess"));
      navigate("/GetUser");
    } catch (error) {
      message.error(t("userCreateFailed"));
      console.error("Error creating user:", error);
    }
  };
  const formFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "none",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const formSelect = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": {
      border: "none",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("addNewMember")}
          </Typography>
          <Grid container spacing={3}>
            {/* Username Field */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("username")}
                </Typography>
                <TextField
                  fullWidth
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  variant="outlined"
                  placeholder={t("usernamePlaceholder")}
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* Password Field */}
            <Grid item xs={12} md={6}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("password")}
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                  placeholder={t("passwordPlaceholder")}
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* Role Field */}
            <Grid item xs={12}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("userRole")}
                </Typography>
                <Select
                  fullWidth
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  variant="outlined"
                  displayEmpty
                  required
                  sx={formSelect}
                >
                  <MenuItem value="" disabled>
                    {t("selectRole")}
                  </MenuItem>
                  {userRole !== "admin" && (
                    <MenuItem value="superadmin">
                      {t("role.superadmin")}
                    </MenuItem>
                  )}
                  <MenuItem value="admin">{t("role.admin")}</MenuItem>
                  <MenuItem value="dataentry">{t("role.dataentry")}</MenuItem>
                </Select>
              </CardContent>
            </Grid>
            {/* Branch Field */}
            {userRole === "superadmin" && role !== "superadmin" && (
              <Grid item xs={12}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("branch")}
                  </Typography>
                  <Select
                    fullWidth
                    value={branch_id}
                    onChange={(e) => setBranch_id(e.target.value)}
                    variant="outlined"
                    displayEmpty
                    required
                    sx={formSelect}
                  >
                    <MenuItem value="" disabled>
                      {t("selectBranch")}
                    </MenuItem>
                    <MenuItem value={1}>{t("branch1.1")}</MenuItem>
                    <MenuItem value={2}>{t("branch1.2")}</MenuItem>
                    <MenuItem value={3}>{t("branch1.3")}</MenuItem>
                  </Select>
                </CardContent>
              </Grid>
            )}
            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/GetUser")}
                  sx={{
                    px: 6,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    px: 6,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                    },
                  }}
                >
                  {t("createUser")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddUser;
